import { FC } from 'react';
import { KTCardBody, KTIcon } from '../../../../../../_metronic/helpers';
import { useGetThemes } from '../api/themes.queries';
import { ThemeDto, ThemeModalState } from '../model/types';

interface Props {
	setOpenModal: (open: ThemeModalState | null) => void;
}

const ThemeCard: FC<{ theme: ThemeDto; setOpenModal: Props['setOpenModal'] }> = ({
	theme,
	setOpenModal,
}) => {
	return (
		<div
			className='card card-bordered'
			style={{ borderRadius: '12px', overflow: 'hidden' }}
		>
			{/* Preview */}
			<div
				style={{
					background: theme.background,
					border: `1px solid ${theme.border}`,
					borderRadius: '8px 8px 0 0',
					height: '100px',
					padding: '10px 12px 8px',
					display: 'flex',
					flexDirection: 'column',
					gap: '8px',
				}}
			>
				{/* Color dots */}
				<div style={{ display: 'flex', gap: '6px' }}>
					{[theme.background, theme.surface, theme.primary, theme.text].map(
						(c, i) => (
							<div
								key={i}
								style={{
									width: '14px',
									height: '14px',
									borderRadius: '50%',
									background: c,
									border: '1px solid rgba(255,255,255,0.15)',
									flexShrink: 0,
								}}
							/>
						)
					)}
				</div>

				{/* Mock cards */}
				<div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', flex: 1 }}>
					{[1, 2].map(i => (
						<div
							key={i}
							style={{
								background: theme.surface,
								border: `1px solid ${theme.border}`,
								borderRadius: '6px',
								overflow: 'hidden',
							}}
						>
							<div style={{ height: '20px', background: theme.surface_muted }} />
							<div
								style={{
									padding: '4px 6px',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'space-between',
								}}
							>
								<div
									style={{
										height: '4px',
										width: '50%',
										borderRadius: '2px',
										background: theme.text,
										opacity: 0.3,
									}}
								/>
								<div
									style={{
										width: '10px',
										height: '10px',
										borderRadius: '3px',
										background: theme.primary,
									}}
								/>
							</div>
						</div>
					))}
				</div>
			</div>

			{/* Info + actions */}
			<div className='px-4 py-3 d-flex align-items-center justify-content-between'>
				<div style={{ minWidth: 0 }}>
					<div className='fw-bold fs-7 text-gray-800 text-truncate'>{theme.name}</div>
					<div className='text-muted fs-8 text-truncate'>{theme.description}</div>
				</div>
				<div className='d-flex gap-1 ms-2 flex-shrink-0'>
					<button
						className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm'
						onClick={() => setOpenModal({ type: 'edit', data: theme })}
						title='Редактировать'
					>
						<KTIcon iconName='pencil' className='fs-4' />
					</button>
					<button
						className='btn btn-icon btn-bg-light btn-active-color-danger btn-sm'
						onClick={() => setOpenModal({ type: 'delete', data: theme })}
						title='Удалить'
					>
						<KTIcon iconName='trash' className='fs-4' />
					</button>
				</div>
			</div>
		</div>
	);
};

const ThemesGrid: FC<Props> = ({ setOpenModal }) => {
	const { data, isLoading } = useGetThemes();
	const themes = data?.data ?? [];

	return (
		<KTCardBody className='py-6'>
			<div className='d-flex justify-content-between align-items-center mb-6'>
				<h3 className='fw-bold fs-5 text-gray-800 mb-0'>
					Темы
					{data?.total !== undefined && (
						<span className='text-muted fw-normal fs-7 ms-2'>({data.total})</span>
					)}
				</h3>
				<button
					type='button'
					className='btn btn-primary btn-sm'
					disabled={isLoading}
					onClick={() => setOpenModal({ type: 'create', data: null })}
				>
					{isLoading ? (
						<span className='spinner-border spinner-border-sm me-2' role='status' />
					) : (
						<KTIcon iconName='plus' className='fs-2 ps-1 me-1' />
					)}
					Создать тему
				</button>
			</div>

			{isLoading ? (
				<div className='d-flex justify-content-center py-10'>
					<span className='spinner-border spinner-border-sm text-primary' role='status' />
				</div>
			) : themes.length === 0 ? (
				<div className='text-center text-muted py-10 fs-6'>Нет тем</div>
			) : (
				<div
					style={{
						display: 'grid',
						gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
						gap: '16px',
					}}
				>
					{themes.map(theme => (
						<ThemeCard key={theme.uuid} theme={theme} setOpenModal={setOpenModal} />
					))}
				</div>
			)}
		</KTCardBody>
	);
};

export { ThemesGrid };
